<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { SidebarProvider } from 'next/polaris/ui/sidebar';
import { useSidebarKeyboardShortcuts } from './useSidebarKeyboardShortcuts';
import { useSidebarResize } from './provider';
import { useSidebarMenu } from './useSidebarMenu';
import GlobalSidebar from './GlobalSidebar.vue';
import ContextSidebar from './ContextSidebar.vue';
import SidebarTopbar from './SidebarTopbar.vue';
import { useTopbarTrail } from './useTopbarTrail';

const props = defineProps({
  isMobileSidebarOpen: { type: Boolean, default: false },
});

const emit = defineEmits([
  'closeKeyShortcutModal',
  'openKeyShortcutModal',
  'showCreateAccountModal',
  'closeMobileSidebar',
  'openMobileSidebar',
]);

// The dashboard's frame: the two tiers of the Polaris shell and, beside them, the top bar over the page (the
// default slot, which Dashboard.vue fills). Same props and events as the old one-tier sidebar, plus
// `openMobileSidebar` and the `topbar-actions` slot.
// No attrs reach the registry provider (Dashboard still passes an old `toggleAccountModal` listener): in dev, an
// attr on SidebarProvider makes its tooltip provider re-render out of turn when tier 1 collapses, and the patch
// breaks ("Cannot read properties of undefined (reading 'el')"). That is also why the shell's widths (kit,
// ProductShell.vue: 196px with labels, a 44px rail) go in as important classes and not as a style: the provider
// sets its own defaults inline.
defineOptions({ inheritAttrs: false });

useSidebarKeyboardShortcuts(show => {
  if (show) emit('openKeyShortcutModal');
  else emit('closeKeyShortcutModal');
});

const { t } = useI18n();
const { isOnChatwootCloud } = useAccount();
const isACustomBrandedInstance = useMapGetter(
  'globalConfig/isACustomBrandedInstance'
);
const isRTL = useMapGetter('accounts/isRTL');

const { areas, activeArea, isItemActive } = useSidebarMenu();
const { trail } = useTopbarTrail(activeArea, isItemActive);

// Tier 1 collapsed or not: the same user setting the old sidebar used (a width below the threshold).
const { isCollapsed, snapToCollapsed, snapToExpanded } = useSidebarResize();
const setTierOneOpen = value => (value ? snapToExpanded() : snapToCollapsed());

// Tier 2 open or put away, remembered per user.
const { uiSettings, updateUISettings } = useUISettings();
const isContextOpen = computed(
  () => uiSettings.value.context_sidebar_open !== false
);
const setContextOpen = value =>
  updateUISettings({ context_sidebar_open: value });

// Choosing an area from tier 1 brings tier 2 back if it was put away (and goes to that area, as any area click
// does). The way back that stays on the current page is the section button of the top bar.
const onSelectArea = () => {
  if (!isContextOpen.value) setContextOpen(true);
};
// Focus stays on the top bar's section button, which is what toggles tier 2.
const toggleContext = () => setContextOpen(!isContextOpen.value);
</script>

<template>
  <SidebarProvider
    :open="!isCollapsed"
    class="w-full min-w-0 min-h-0 h-full flex-1 bg-polaris-background text-polaris-foreground font-polaris ![--sidebar-width:12.25rem] ![--sidebar-width-icon:2.75rem]"
    @update:open="setTierOneOpen"
  >
    <GlobalSidebar
      :areas="areas"
      :active-area="activeArea"
      :is-item-active="isItemActive"
      :side="isRTL ? 'right' : 'left'"
      :is-mobile-sidebar-open="props.isMobileSidebarOpen"
      :show-changelog="isOnChatwootCloud && !isACustomBrandedInstance"
      @select-area="onSelectArea"
      @close-mobile-sidebar="emit('closeMobileSidebar')"
      @open-key-shortcut-modal="emit('openKeyShortcutModal')"
      @show-create-account-modal="emit('showCreateAccountModal')"
    />
    <ContextSidebar
      v-if="activeArea"
      :area="activeArea"
      :open="isContextOpen"
      :is-item-active="isItemActive"
    />
    <!-- Beside the two tiers: the top bar over the page, which the dashboard passes in as the default slot. -->
    <div class="flex h-full min-w-0 flex-1 flex-col bg-n-surface-1">
      <SidebarTopbar
        :area="activeArea?.label ?? t('SIDEBAR.AREA_INBOX')"
        :trail="trail"
        :section-open="isContextOpen"
        :has-section="!!activeArea"
        @toggle-section="toggleContext"
        @open-mobile-sidebar="emit('openMobileSidebar')"
      >
        <template #actions>
          <slot name="topbar-actions" />
        </template>
      </SidebarTopbar>
      <slot />
    </div>
  </SidebarProvider>
</template>
